import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import { useApi } from 'hooks/useApi';
import CompanyDocumentApi from 'libs/api/admin/companyDocuments';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import { useNavigate } from 'hooks/useNavigate';
import { getShops } from 'hooks/useShops';
import CompanyDocumentsAdapter from './documents.adapter';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import Helpers from 'helpers';
import moment from 'moment';
import CompanyDocumentCategories from 'libs/api/admin/companyDocumentCategories';
import { useFilter } from '../../../../hooks/useFilter';

const api = new CompanyDocumentApi();
const categoryApi = new CompanyDocumentCategories();

const useDocuments = (isMainPage = false) => {
  const [categories, setCategories] = useState([]);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const { id } = useParams();
  const shops = useRecoilValue(getShops);
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const { filterData } = useFilter();
  const {
    singleItem,
    loading,
    data,
    onChangeTableFilter,
    handleDownloadExcel,
    updateInProcess,
    setUpdateInProcess,
    onDelete,
  } = useApi({
    resourceKey: 'company_documents',
    singleResourceKey: 'company_document',
    api: api,
    isMainPage,
    adapter: CompanyDocumentsAdapter,
  });
  const handleDownloadPdf = useCallback(
    async (url, fileName = 'vacancy_application.pdf') => {
      setUpdateInProcess(true);
      try {
        const response = await api.getPDF(url);
        Helpers.downloadFile(response, fileName);
      } catch (e) {
        UIHelper.serverError();
      } finally {
        setUpdateInProcess(false);
      }
    },
    [],
  );
  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === 'file') {
          if (values[key]) {
            formData.append(
              `company_document[${key}]`,
              values[key][0]?.originFileObj || '',
            );
          }
        } else {
          formData.append(`company_document[${key}]`, values[key]);
        }
      });
      try {
        if (!id) {
          await api.create({
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, {
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        console.log('e', e);
        const errors = e.response.data || [];
        form.setFields(ErrorHelper.getErrorsForForm(errors));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id, shoppingCenters],
  );

  const onClickFile = useCallback(async (filePath) => {
    const fullPath = Helpers.getFilePath(filePath);
    window.open(fullPath, '_blank');
  }, []);

  const columns = useMemo(() => {
    return [
      {
        title: t('Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Catena'),
        dataIndex: 'chains.title',
        sorter: () => {},
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
      },
      {
        title: t('Data'),
        dataIndex: 'publishing_date',
        sorter: () => {},
        render: (d) => UIHelper.formatedDate(d),
      },
      {
        title: t('Categoria'),
        dataIndex: 'company_document_categories.title',
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 220,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false ||
            row.file) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.file && (
                <Col>
                  <Button
                    type={'green'}
                    icon={<FilePdfOutlined />}
                    onClick={() => handleDownloadPdf(row.file, row.file_name)}
                  />
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [pathname, t, handleDownloadPdf, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Categorie'),
        type: 'select',
        name: 'category',
        values: categories.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
    ],
    [t, categories, filterData],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem?.id) {
      form.setFields([
        {
          name: 'title',
          value: singleItem.title,
        },
        {
          name: 'publishing_date',
          value:
            singleItem.publishing_date && moment(singleItem.publishing_date),
        },
        {
          name: 'file',
          value: singleItem.file_present,
        },
        {
          name: 'company_document_category_id',
          value: singleItem.category?.id,
        },
        {
          name: 'company_id',
          value: singleItem.company?.id,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { company_document_categories },
        } = await categoryApi.list({
          params: {
            per: -1,
          },
        });
        setCategories(company_document_categories);
      } catch (e) {}
    })();
  }, []);

  return {
    columns,
    singleItem,
    form,
    shoppingCenters,
    onClickFile,
    data,
    loading,
    filters,
    updateInProcess,
    onChangeTableFilter,
    handleCreateOrUpdate,
    handleDownloadExcel,
    handleDownloadPdf,
    onDelete,
    categories,
    shops: filterData?.allCompanies,
  };
};

export default useDocuments;
